import type { CommandProps } from '@magicschool/spellbook/components/Command';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { SearchInput, type SearchInputProps } from '@magicschool/spellbook/components/SearchInput';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { Spinner, type SpinnerProps } from '@magicschool/spellbook/components/Spinner';
import { Text } from '@magicschool/spellbook/components/Text';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { mapOrderedSlots } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Command as CommandPrimitive } from 'cmdk';
import { type ComponentProps, useEffect, useRef, useState } from 'react';

export const CommandInputSlot = Slot<Omit<ComponentProps<typeof CommandPrimitive.Input>, 'size'> & SearchInputProps>({
  displayName: 'Command.Input',
  renderDefault: ({ asChild: _, className, onClearSearch, value, onValueChange, ...props }) => {
    const handleValueChange = (newValue: string) => {
      onValueChange?.(newValue);
    };

    const handleClearSearch = () => {
      handleValueChange('');
      onClearSearch?.();
    };

    const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
      const newValue = (e.target as HTMLInputElement).value;
      handleValueChange(newValue);
    };

    return (
      <CommandPrimitive.Input data-spellbook-name="command-input" asChild value={value} onValueChange={onValueChange}>
        <SearchInput className={className} {...props} value={value} onClearSearch={handleClearSearch} onInput={handleInputChange} />
      </CommandPrimitive.Input>
    );
  },
  renderOverride: ({ asChild: _, size: __, ...props }) => {
    return <CommandPrimitive.Input data-spellbook-name="command-input" {...props} asChild />;
  },
});

export const CommandListSlot = Slot<
  ComponentProps<typeof CommandPrimitive.List> & {
    isLoading?: CommandProps['isLoading'];
    text?: CommandProps['text'];
    descriptionOrientation?: CommandProps['descriptionOrientation'];
    multiple?: CommandProps['multiple'];
  }
>({
  displayName: 'Command.List',
  preventOverride: true,
  renderDefault: ({ className, children, descriptionOrientation, isLoading, multiple, text = {}, ...props }) => {
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const selectedItem = (mutation.target as Element).querySelector('[aria-selected="true"]');
            if (selectedItem) {
              const groupHeader = selectedItem.closest('[role="group"]');
              if (groupHeader) {
                groupHeader.scrollIntoView({ block: 'start' });
                return;
              }

              selectedItem.scrollIntoView({ block: 'start' });
              return;
            }
          }
        });
      });
      if (listRef.current) {
        observer.observe(listRef.current, {
          childList: true,
          subtree: true,
        });
      }

      return () => {
        observer.disconnect();
      };
    }, [children, isLoading]);

    const { slots, rest } = useSlots(children, {
      spinner: { Component: CommandSpinnerSlot },
      empty: { Component: CommandEmptySlot, props: { text } },
    });

    const { orderedSlots } = useOrderedSlots(rest, {
      item: { Component: CommandItemSlot, props: { descriptionOrientation, multiple } },
      group: { Component: CommandGroupSlot, props: { descriptionOrientation, multiple } },
    });

    return (
      <CommandPrimitive.List
        ref={listRef}
        data-spellbook-name="command-list"
        {...props}
        className={cn('sm:min-w-form-field-min w-full overflow-x-hidden overflow-y-auto', className)}
      >
        {isLoading ? (
          <div className="flex px-4 items-center justify-center py-2">{slots.spinner}</div>
        ) : (
          <>
            {slots.empty}
            {mapOrderedSlots(orderedSlots)}
          </>
        )}
      </CommandPrimitive.List>
    );
  },
});

export const CommandEmptySlot = Slot<ComponentProps<typeof CommandPrimitive.Empty> & { text?: CommandProps['text'] }>({
  displayName: 'Command.Empty',
  preventOverride: true,
  isRenderedByDefault: true,
  renderDefault: ({ children, className, text = {}, ...props }) => {
    const { noResultsText = 'No results found' } = text;

    return (
      <CommandPrimitive.Empty data-spellbook-name="command-empty" className={cn('flex p-4', className)} {...props}>
        <Text variant="caption-1" color="default-soft">
          {children || noResultsText}
        </Text>
      </CommandPrimitive.Empty>
    );
  },
});

export const CommandGroupSlot = Slot<
  Omit<ComponentProps<typeof CommandPrimitive.Group>, 'heading'> & {
    label: string;
    multiple?: CommandProps['multiple'];
    descriptionOrientation?: CommandProps['descriptionOrientation'];
  }
>({
  displayName: 'Command.Group',
  preventOverride: true,
  renderDefault: ({ className, children, descriptionOrientation, label, multiple, id: providedId, ...props }) => {
    const id = useId(providedId);

    const { orderedSlots } = useOrderedSlots(children, {
      item: { Component: CommandItemSlot, props: { descriptionOrientation, multiple } },
    });

    return (
      <CommandPrimitive.Group
        data-spellbook-name="command-group"
        className={cn('border-t border-t-border-default pt-2 -mt-px', className)}
        {...props}
      >
        {label && (
          <Text variant="caption-1" color="default-soft" className="py-2 px-4 flex" id={id} role="presentation">
            {label}
          </Text>
        )}
        {mapOrderedSlots(orderedSlots)}
      </CommandPrimitive.Group>
    );
  },
});

const itemCheckboxVariants = cva('flex-0 inline-flex h-4 w-4 m-1 items-center justify-center min-w-4 min-h-4 rounded-sm border-[1.5px]', {
  variants: {
    isChecked: {
      true: [
        'bg-primary-solid-background-hover',
        'border-transparent',
        'text-primary-solid-foreground-text',
        'group-hover:bg-primary-solid-background-hover',
        'group-active:bg-primary-solid-background-active',
      ],
      false: 'bg-surface-background-0 border-checkbox group-hover:border-checkbox-hover group-hover:bg-surface-background-2',
    },
    disabled: {
      true: 'border-disabled-foreground text-disabled-foreground',
    },
  },
});

const itemLabelVariants = cva(
  [
    'flex',
    'overflow-hidden',
    'text-default-text-strong',
    /**
     * @TODO [SPE-16] Centralize "Selected" Color Tokens into Shared Style Utilities
     * https://linear.app/magicschool/issue/SPE-16/centralize-selected-color-tokens-into-shared-style-utilities
     */
    'group-data-[checked=true]:text-selected-default-foreground',
    'group-data-[checked=true]:group-hover:text-selected-default-foreground-hover',
    'group-data-[checked=true]:group-focus-visible:text-selected-default-foreground-hover',
    'group-data-[checked=true]:group-active:text-selected-default-foreground-active',
    'group-data-[disabled=true]:!text-disabled-foreground',
    'group-data-[disabled=true]:!cursor-not-allowed',
  ],
  {
    variants: {
      descriptionOrientation: {
        horizontal: 'flex-row gap-2 items-baseline [&_span]:truncate',
        vertical: 'flex-col grow-1 gap-0 items-start',
      },
    },
  },
);

export const CommandItemSlot = Slot<
  Omit<ComponentProps<typeof CommandPrimitive.Item>, 'value'> &
    VariantProps<typeof itemLabelVariants> & {
      selected?: boolean;
      label?: string;
      description?: string;
      value: string;
      multiple?: CommandProps['multiple'];
    }
>({
  displayName: 'Command.Item',
  renderDefault: ({
    className,
    children,
    disabled,
    description,
    descriptionOrientation,
    label,
    value,
    selected,
    keywords = [],
    onSelect,
    multiple,
    ...props
  }) => {
    const optionRef = useRef<HTMLDivElement>(null);
    const [isSelected, setIsSelected] = useState(selected);

    if (!value) {
      console.error('Command.Item requires a `value` prop to be set.');
    }

    useEffect(() => {
      if (value === undefined || value === null) {
        console.error('Command.Item requires a `value` prop to be set.');
      }
    }, [value]);

    useEffect(() => {
      setIsSelected(selected);
    }, [selected]);

    return (
      <CommandPrimitive.Item
        data-spellbook-name="command-item"
        className={cn(
          [
            'flex',
            'cursor-pointer',
            'items-center',
            'justify-between',
            'outline-none',
            'ps-4',
            'pe-6',
            'hover:bg-surface-overlay-1',
            'data-[selected=true]:not-data-[checked=true]:bg-surface-overlay-1',
            'data-[checked=true]:bg-selected-default-background',
            'data-[checked=true]:hover:bg-selected-default-background-hover',
            'data-[checked=true]:active:bg-selected-default-background-active',
            'data-[checked=true]:focus-visible:bg-selected-default-background-hover',
            'data-[disabled=true]:!cursor-not-allowed',
            'data-[disabled=true]:!bg-transparent',
            'py-1.5',
            'gap-2',
            'group',
          ],
          className,
        )}
        ref={optionRef}
        {...props}
        value={value}
        keywords={keywords}
        onSelect={onSelect}
        data-checked={isSelected}
        aria-checked={isSelected}
        disabled={disabled}
      >
        {multiple && (
          <span className={itemCheckboxVariants({ isChecked: isSelected, disabled })}>
            {isSelected && <Icon name="check" color="inherit" size="xs" />}
          </span>
        )}
        {children ? (
          <span className="flex-1 min-w-0">{children}</span>
        ) : (
          label && (
            <span className="flex-1 min-w-0">
              <span className="flex items-center gap-2 overflow-hidden">
                <span className={itemLabelVariants({ descriptionOrientation })}>
                  <Text variant="body-2" element="span" color="inherit" className="max-w-full shrink-0">
                    {label}
                  </Text>
                  {description && (
                    <Text variant="caption-1" color="default-soft">
                      {description}
                    </Text>
                  )}
                </span>
              </span>
            </span>
          )
        )}

        {isSelected && !multiple && <Icon name="check" color="primary" size="sm" />}
      </CommandPrimitive.Item>
    );
  },
});

export const CommandSpinnerSlot = Slot<SpinnerProps>({
  displayName: 'Command.Spinner',
  isRenderedByDefault: true,
  renderDefault: ({ color = 'primary', ...props }) => {
    return <Spinner data-spellbook-name="command-spinner" color={color} {...props} />;
  },
});
